import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, type SxProps, type Theme, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { motion, useReducedMotion } from "framer-motion";
import type { DateTime } from "luxon";
import { type FC, type KeyboardEvent, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { describeBuckets, useDescribeBuckets } from "@/components/staffing/WeekSchedule/PunchActionCenter/describeBuckets";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { BADGE, FONT_SIZE, ICON_SIZE, INK, MOTION, SURFACE } from "@/theme/tokens";
import { type PunchSummary, type PunchSummaryCategory, WEEKLY_PUNCH_SUMMARY_CATEGORY_ORDER } from "@/utils/models";
import { PUNCH_SUMMARY_CATEGORY_DOT_HEX } from "@/utils/punchSummary";

type Props = {
	dayDate: DateTime;
	/** `rowElement` is the clicked row itself, so the surface it opens can align to it. */
	onClick: (category: PunchSummaryCategory, rowElement: HTMLElement) => void;
	summary: PunchSummary | null;
};

const containerSx = { pt: 0.5, pb: 1, bgcolor: SURFACE, flex: 1 } as const satisfies SxProps<Theme>;
const placeholderSx = { px: 1, py: 1, bgcolor: SURFACE, flex: 1 } as const satisfies SxProps<Theme>;

const rowSx = (color: string): SxProps<Theme> => ({
	display: "flex",
	alignItems: "center",
	gap: 0.75,
	px: 1,
	// Tight on purpose: the footer repeats on all seven day cards, so each row's height is paid seven
	// times over before the grid below comes into view (APP-87).
	py: 0.25,
	cursor: "pointer",
	borderRadius: 1,
	transition: (theme) => theme.transitions.create("background-color", { duration: MOTION.DURATION.HOVER }),
	"@media (hover: hover) and (pointer: fine)": {
		"&:hover": {
			backgroundColor: alpha(color, 0.08),
			"& .punch-row-chevron": { color, transform: "translateX(2px)" },
		},
	},
	"&:active": { transform: "scale(0.99)" },
	"@media (prefers-reduced-motion: reduce)": { "&:active": { transform: "none" } },
});

const countPillSx = (color: string): SxProps<Theme> => ({
	minWidth: BADGE.PILL_SIZE,
	height: BADGE.PILL_SIZE,
	px: 0.5,
	borderRadius: `${BADGE.PILL_RADIUS}px`,
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	flexShrink: 0,
	bgcolor: alpha(color, 0.14),
	color,
	fontSize: FONT_SIZE.LABEL_XS,
	fontWeight: 600,
});

const dotSx = (color: string): SxProps<Theme> => ({
	width: 8,
	height: 8,
	borderRadius: "50%",
	bgcolor: color,
	flexShrink: 0,
});

export const PunchSummaryFooter: FC<Props> = ({ dayDate, onClick: onCategoryClick, summary }) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const { track } = useAnalytics();
	const describeBucket = useDescribeBuckets();
	const reduceMotion = useReducedMotion();

	const visibleCategories = useMemo<PunchSummaryCategory[]>(() => {
		if (!summary) return [];
		return WEEKLY_PUNCH_SUMMARY_CATEGORY_ORDER.filter((cat) => describeBuckets(summary, cat).hasEntries);
	}, [summary]);

	if (!summary) {
		return (
			<Box data-testid={`punch-summary-footer-loading-${dayDate.toISODate()}`} sx={placeholderSx}>
				<Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
					{t("punchSummary.title")}…
				</Typography>
			</Box>
		);
	}

	if (!visibleCategories.length) {
		return (
			<Box data-testid={`punch-summary-footer-empty-${dayDate.toISODate()}`} sx={placeholderSx}>
				<Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
					{t("punchSummary.empty")}
				</Typography>
			</Box>
		);
	}

	const openCategory = (category: PunchSummaryCategory, rowElement: HTMLElement): void => {
		track("punch_summary_open_category", { category, date: dayDate.toISODate() });
		onCategoryClick(category, rowElement);
	};

	const onRowKeyDown = (event: KeyboardEvent<HTMLDivElement>, category: PunchSummaryCategory): void => {
		if (event.key !== "Enter" && event.key !== " ") return;
		event.preventDefault();
		openCategory(category, event.currentTarget);
	};

	return (
		<Box data-testid={`punch-summary-footer-${dayDate.toISODate()}`} sx={containerSx}>
			<Typography
				variant="caption"
				sx={{
					textTransform: "uppercase",
					fontWeight: 700,
					fontSize: FONT_SIZE.LABEL_XS,
					letterSpacing: "0.06em",
					color: theme.palette.text.secondary,
					px: 1,
					display: "block",
					mb: 0.5,
				}}
			>
				{t("punchSummary.title")}
			</Typography>
			{visibleCategories.map((cat, index) => {
				const description = describeBucket(summary, cat);
				const color = PUNCH_SUMMARY_CATEGORY_DOT_HEX[cat];
				const label = t(description.labelKey);
				return (
					<motion.div
						key={cat}
						initial={reduceMotion ? false : { opacity: 0, y: 4 }}
						animate={{ opacity: 1, y: 0 }}
						transition={
							reduceMotion
								? { duration: 0 }
								: {
										duration: MOTION.DURATION.ENTRANCE_SM / 1000,
										ease: MOTION.EASE_OUT_POINTS,
										delay: Math.min(index * MOTION.STAGGER.STEP, MOTION.STAGGER.MAX) / 1000,
									}
						}
					>
						<Box
							sx={rowSx(color)}
							data-testid={`punch-summary-row-${cat}`}
							role="button"
							tabIndex={0}
							aria-label={`${label} · ${description.employeeCount}`}
							onClick={(event) => openCategory(cat, event.currentTarget)}
							onKeyDown={(event) => onRowKeyDown(event, cat)}
						>
							<Box sx={dotSx(color)} />
							<Typography sx={{ color: INK.PRIMARY, fontSize: FONT_SIZE.SECONDARY, flex: 1, minWidth: 0 }} noWrap>
								{label}
							</Typography>
							<Box sx={countPillSx(color)}>{description.employeeCount}</Box>
							<ChevronRightIcon
								className="punch-row-chevron"
								sx={{
									fontSize: ICON_SIZE.INLINE,
									color: INK.DISABLED,
									transition: (theme) => theme.transitions.create(["color", "transform"], { duration: MOTION.DURATION.HOVER }),
								}}
							/>
						</Box>
					</motion.div>
				);
			})}
		</Box>
	);
};
